import { inject, Injectable, signal } from '@angular/core';
import { EskoAgentContextService } from '@shared/modules/esko-agent/services/esko-agent-context.service';
import { AiAgentSession, AiAgentType, AiRole } from '@shared/models/ai.model';
import { ZefApi } from '@shared/services/zef-api.service';
import { catchError, takeUntil } from 'rxjs/operators';
import { of, Subscription, switchMap } from 'rxjs';
import { BillingState } from '@shared/states/billing.state';
import { LicenseFeature } from '@shared/enums/license-feature.enum';
import { selectStable } from '@shared/signal/select-stable.signal';
import { assertArray } from '@shared/utilities/array.utilities';

@Injectable()
export class SupportChatAgentContext extends EskoAgentContextService {
  readonly agentType = AiAgentType.Support;

  readonly canActivate = selectStable(BillingState.featureActive(LicenseFeature.ChatSupport));

  readonly agentWelcome = signal($localize`Hello! How can I help you today?`);

  readonly markdown = false;

  readonly api = inject(ZefApi);

  readonly defaultTitle = $localize`Support Chat`;

  private messageSending?: Subscription;

  override async sendMessage(message: string): Promise<void> {
    const id = parseInt(this.sessionId()?.replace(`${this.agentType}-`, ''));

    if (Number.isNaN(id)) {
      this.startNewSession($localize`Support Chat`);
    } else {
      this.newSession$.next(null);
    }

    this.thinking.set(true);

    await this.addMessages({ message, role: AiRole.User });

    this.messageSending?.unsubscribe();

    this.messageSending = this.api
      .post<AiAgentSession>(`cs/chat`, {
        messages: this.messages(),
        url: window.location.href,
        ...(id && { id }),
      })
      .pipe(
        catchError(() =>
          of({
            messages: [
              { message: $localize`I'm sorry, something went wrong, please try again later`, role: AiRole.System },
            ],
          } as AiAgentSession),
        ),
        switchMap((response) => {
          const responseMessages = assertArray(response?.messages);

          const messages = responseMessages
            .filter((message) => message?.newAssistantReply && !!message.message)
            .map((message) => ({ message: message.message.replace(/\[doc([^[\]]+)]/g, ''), role: AiRole.Assistant }));

          if (response.id != null) {
            this.sessionId$.next(`${this.agentType}-${response.id.toString()}`);
          }

          return this.addMessages(messages);
        }),
        catchError(() => of(undefined)),
        takeUntil(this.cancelRequest$),
      )
      .subscribe(() => {
        this.thinking.set(false);
      });
  }
}
